/**
 * ConsentExternalUploadDialog (PROD-1243 / Case 3) — "this consent was signed
 * outside our app".
 *
 * A clinic that signs on its OWN e-sign tool (Microsoft pen pad, a vendor e-sign
 * service) or prints → wet-signs → scans uploads the finished signed PDF here. The
 * user has already picked the consent TEMPLATE in the New File popup, which classifies
 * the document (type + FHIR scope/category); this dialog only collects the signed PDF
 * and posts it to `POST /api/Consent/IngestExternal` (multipart). The server stores it
 * verbatim as the `Type="Form"` patient document and creates an ACTIVE ConsentRecord
 * with capability `external` — NO fill / stamp / PAdES (we don't own the document).
 *
 * Not a destructive action, so this is a shadcn `Dialog` (not AlertDialog). PDF-only,
 * accepted by extension OR MIME (some OSes report an empty/octet-stream MIME for a valid
 * .pdf chosen via Browse — mirror ConsentDocumentBuilder.onFile). Save is gated until a
 * PDF is chosen; server errors surface through the global mutation toast.
 */
import { useId, useState } from 'react';
import { X } from 'lucide-react';
import { Button, Icon, Modal } from '@scripsteam/scrips-signal-ds';
import { DropFileRow, DropZone } from '@/shared/ui/components/drop-zone';
import { useAuthStore } from '@/auth/auth-store';
import type { ConsentTemplate } from '@/consent/types/consent-template-api';
import { useIngestExternalConsent } from '@/consent/services';
// The ingested PDF is stored as a patient document (Type="Form"), so it follows the
// same org-scoped document upload limit as the New File flow (PROD-939) — not a
// consent-specific cap (the IngestExternal endpoint enforces no size of its own).
import {
  DEFAULT_DOCUMENT_MAX_UPLOAD_MB,
  isFileSizeGreater,
  oversizeMessage,
} from '@/documents/lib/file-allowlist';
// PROD-2743 design sheet §3 (icons): DS names ON only (?ef=frameV2); OFF keeps today's lucide icon, unchanged.

// FHIR classification default — mirrors ConsentFillSignPopup CREATE_DEFAULTS (Samer
// 2026-06-17: scope=treatment for a "consent to be treated"; per-template taxonomy is
// the PROD-1051 follow-up). The chosen template still pins name + version-hash server-side.
const CLASSIFICATION = {
  consentType: 'clinical',
  scopeCode: 'treatment',
  categoryCode: '59284-0',
  categoryDisplay: 'Patient Consent',
  categorySystem: 'http://loinc.org',
};

export interface ConsentExternalUploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patientId: string;
  /** Links the consent to an encounter (appointment upload) → encounter-scoped Form doc. */
  encounterId?: string | null;
  /** The consent template this externally-signed document corresponds to (classification source). */
  template: ConsentTemplate;
  actorId?: string | null;
  actorName?: string | null;
  /** Fired after a successful ingest (new Type="Form" doc + active consent). */
  onIngested?: () => void;
}

export function ConsentExternalUploadDialog({
  open,
  onOpenChange,
  patientId,
  encounterId,
  template,
  actorId,
  actorName,
  onIngested,
}: ConsentExternalUploadDialogProps) {
  const [file, setFile] = useState<File | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputId = useId();
  const ingest = useIngestExternalConsent(patientId);

  // Same org-scoped document limit the New File flow uses (PROD-939); falls back to
  // the documents-module default when auth-store has no per-org override.
  const maxUploadMb =
    useAuthStore((s) => s.user?.maxDocumentUploadLimit) ?? DEFAULT_DOCUMENT_MAX_UPLOAD_MB;

  function pick(f: File | undefined) {
    if (!f) return;
    const isPdf = f.type === 'application/pdf' || /\.pdf$/i.test(f.name);
    if (!isPdf) {
      setLocalError('Choose a PDF file.');
      return;
    }
    if (isFileSizeGreater(f.size, maxUploadMb)) {
      setLocalError(oversizeMessage(maxUploadMb));
      return;
    }
    setLocalError(null);
    setFile(f);
  }

  function reset() {
    setFile(null);
    setLocalError(null);
  }

  function handleUpload() {
    if (!file || ingest.isPending) return;
    ingest.mutate(
      {
        patientId,
        encounterId: encounterId ?? null,
        consentTemplateId: template.id,
        ...CLASSIFICATION,
        actorId: actorId ?? null,
        actorName: actorName ?? null,
        file,
      },
      {
        onSuccess: () => {
          reset();
          onIngested?.();
          onOpenChange(false);
        },
      },
    );
  }

  return (
    <Modal
      open={open}
      onClose={() => {
        reset();
        onOpenChange(false);
      }}
      title="Upload a signed consent"
      description="Use this when the consent was signed outside the app. The signed PDF is filed
            as-is under Docs › Forms — no in-app filling or stamping."
      size="md"
      footer={<><Button
            type="button"
            variant="secondary"
            onClick={() => onOpenChange(false)}
            disabled={ingest.isPending}
          >
            Cancel
          </Button>
          <Button type="button" onClick={handleUpload} disabled={!file || ingest.isPending}>
            {ingest.isPending ? 'Uploading…' : 'Upload consent'}
          </Button></>}
    >
        

        {/* The design's PaConsentUpload `.root`: the chip, the chooser or chosen file and the error line, 12 apart. */}
        <div data-slot="consent-upload-body">
          {/* chosen template (classification source) */}
          {/* The design's template chip (PaConsentUpload `.chip`): the active-blue tint, the file glyph, the name strong. */}
          <div data-slot="consent-upload-template">
            <Icon name="file" size={16} color="currentColor" aria-hidden />
            <span data-slot="consent-upload-template-name">{template.name}</span>
          </div>

          {/* file picker / chosen file */}
          {file ? (
            <DropFileRow
              name={file.name}
              action={
                <button
                  type="button"
                  aria-label="Remove file"
                  onClick={reset}
                  disabled={ingest.isPending}
                  className="icon-action"
                >
                  <X className="size-4" />
                </button>
              }
            />
          ) : (
            // Drop zone = a native <label> with the <input> NESTED INSIDE it (implicit
            // association), byte-for-byte the structure of new-file-popup's working
            // fileDropZone. The whole label is the click target → opens the OS picker as a
            // browser-trusted gesture; the input is `sr-only` (NOT `hidden`/display:none,
            // which is "not rendered" and silently no-ops label activation in several
            // browsers). No JS `inputRef.click()`, no htmlFor/getElementById indirection.
            <DropZone
              as="label"
              look="card"
              dragging={dragActive}
              onDragOver={(e) => {
                e.preventDefault();
                setDragActive(true);
              }}
              onDragLeave={() => setDragActive(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragActive(false);
                pick(e.dataTransfer.files?.[0]);
              }}
              title="Choose the signed PDF"
              hint={`PDF only · up to ${maxUploadMb} MB`}
            >
              <input
                id={fileInputId}
                type="file"
                accept="application/pdf,.pdf"
                className="sr-only"
                onChange={(e) => {
                  pick(e.target.files?.[0]);
                  e.currentTarget.value = '';
                }}
              />
            </DropZone>
          )}

          {localError ? (
            <p data-slot="drop-zone-error" role="alert">{localError}</p>
          ) : null}
        </div>
      </Modal>
  );
}

ConsentExternalUploadDialog.displayName = 'ConsentExternalUploadDialog';

export default ConsentExternalUploadDialog;
